import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { DashboardStats } from '../../services/dashboardService';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend);

interface Props {
  stats: DashboardStats | null;
}

export default function TicketStatusBreakdown({ stats }: Props) {
  const { theme } = useTheme();
  const { t } = useTranslation();

  if (!stats) {
    return (
      <div className={`rounded-lg shadow p-6 ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}>
        <div className={`h-6 w-40 rounded mb-4 animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
        <div className={`h-64 w-full rounded animate-pulse ${
          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
        }`}></div>
      </div>
    );
  }

  const statusData = stats.ticketsByStatus;
  const total = Object.values(statusData).reduce((sum, count) => sum + count, 0);

  const data = {
    labels: [
      t('tickets.status.new', 'New'),
      t('tickets.status.processing', 'Processing'),
      t('tickets.status.resolved', 'Resolved'),
      t('tickets.status.closed', 'Closed'),
    ],
    datasets: [
      {
        data: [
          statusData.new,
          statusData.processing,
          statusData.resolved,
          statusData.closed,
        ],
        backgroundColor: [
          theme === 'dark' ? '#EF4444' : '#DC2626', // Red for new
          theme === 'dark' ? '#F59E0B' : '#D97706', // Orange for processing
          theme === 'dark' ? '#10B981' : '#059669', // Green for resolved
          theme === 'dark' ? '#6B7280' : '#4B5563', // Gray for closed
        ],
        borderColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        borderWidth: 2,
        hoverBorderWidth: 3,
        hoverOffset: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          color: theme === 'dark' ? '#E5E7EB' : '#374151',
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          generateLabels: function(chart: any) {
            const data = chart.data;
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label: string, i: number) => {
                const value = data.datasets[0].data[i];
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return {
                  text: `${label}: ${value} (${percentage}%)`,
                  fillStyle: data.datasets[0].backgroundColor[i],
                  strokeStyle: data.datasets[0].borderColor,
                  lineWidth: data.datasets[0].borderWidth,
                  fontColor: theme === 'dark' ? '#E5E7EB' : '#374151',
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        backgroundColor: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        titleColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        bodyColor: theme === 'dark' ? '#E5E7EB' : '#374151',
        borderColor: theme === 'dark' ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            return `${context.label}: ${value} tickets (${percentage}%)`;
          },
        },
      },
    },
    cutout: '60%',
    elements: {
      arc: {
        borderRadius: 4,
      },
    },
  };

  // Status details for the center info
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'text-red-500';
      case 'processing': return 'text-orange-500';
      case 'resolved': return 'text-green-500';
      case 'closed': return 'text-gray-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'new':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
      case 'processing':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'resolved':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'closed':
        return (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`rounded-lg shadow p-6 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      {/* Header */}
      <div className="mb-6">
        <h3 className={`text-lg font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>
          {t('pageHeaders.dashboard.statusBreakdown', 'Status Breakdown')}
        </h3>
        <p className={`text-sm mt-1 ${
          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {t('pageHeaders.dashboard.statusDescription', 'Distribution of tickets by current status')}
        </p>
      </div>

      {/* Chart Container */}
      <div className="relative">
        <div className="h-64 relative">
          <Doughnut data={data} options={options} />
          
          {/* Center Info */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {total}
              </div>
              <div className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {t('pageHeaders.dashboard.totalTickets', 'Total Tickets')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Summary */}
      <div className="mt-6 space-y-3">
        {Object.entries(statusData).map(([status, count]) => {
          const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0';
          return (
            <div key={status} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`${getStatusColor(status)}`}>
                  {getStatusIcon(status)}
                </div>
                <span className={`text-sm font-medium capitalize ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t(`tickets.status.${status}`, status)}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`text-sm font-semibold ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {count}
                </span>
                <span className={`text-xs ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  ({percentage}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Insights */}
      <div className={`mt-6 p-4 rounded-lg ${
        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
      }`}>
        <h4 className={`text-sm font-medium mb-2 ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
        }`}>
          {t('pageHeaders.dashboard.quickInsights', 'Quick Insights')}
        </h4>
        <div className="space-y-1">
          {statusData.new > 0 && (
            <p className={`text-xs ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              • {statusData.new} {t('pageHeaders.dashboard.newTicketsNeedAttention', 'new tickets need attention')}
            </p>
          )}
          {statusData.processing > 0 && (
            <p className={`text-xs ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              • {statusData.processing} {t('pageHeaders.dashboard.ticketsInProgress', 'tickets currently in progress')}
            </p>
          )}
          {statusData.resolved > 0 && (
            <p className={`text-xs ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              • {statusData.resolved} {t('pageHeaders.dashboard.ticketsAwaitingClosure', 'tickets awaiting closure')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
